import React from 'react';
import { Box, Tooltip } from '@mui/material';
import ColoredBox from 'components/ColoredBox';
import { StyledEcrfElementLabels } from '../Ecrf.styled'
import AdminTools from './AdminTools';
import DateTime from "core/helpers/date/DateTime";
import { DATETIME_FORMAT } from "commons/constants/env";
import { roles } from 'commons/auth/roles';
import { isEmpty } from 'lodash';
import { ElementTypeUtils } from "core/helpers/ElementTypeUtils";
import { useTranslation } from "react-i18next";
import useQueryPanel from "hooks/useQueryPanel";

// ==============================================================
interface Props {
  isCollection: boolean;
  elementData?: {
    updatedAt?: string;
    status?: number;
    data?: any;
    comment?: {
      updatedAt: string;
      content: string;
    };
  };
  element: any;
  ecrfId: string;
  userProfile: {
    roles: string[];
  };
  children: React.ReactNode;
}
// ==============================================================

const ElementBox: React.FC<Props> = ({
  isCollection,
  elementData,
  element,
  ecrfId,
  userProfile,
  children
}) => {
  const { data: queryData } = useQueryPanel();
  const { t } = useTranslation();

  /**
   * Border style data state
   */
  const borderStyleDataState = () => {
    // query panel
    if (queryData?.elementId === element.id) return '5px solid #F44336';

    // required
    if (element?.required) return '3px solid #F44336';

    // no data
    if (displayNoDataInfo()) {
      return '1px solid #F44336';
    }

    // return default border style
    return '1px solid #F7FAFF';
  }

  /**
   * 'No data' controller.
   * If return true will display red border and text 'No data'
   */
  const displayNoDataInfo = () => {
    if (
      ElementTypeUtils.isButton(element.type) ||
      ElementTypeUtils.isCollection(element.type) ||
      element?.options?.variant === 'randNumber'
    ) {
      return false;
    }

    // return status for data content
    return !elementData?.data;
  }

  return (
    <ColoredBox p={2} mb={2} border={borderStyleDataState()} style={{ color: '#060A32' }}>
      {children}

      {/* <--- Display 'No data' label ---> */}
      {!isCollection && displayNoDataInfo() && (
        <Box display="flex" my={1}>
          <Tooltip title={t('no-data-enter-na')} arrow>
            <Box
              fontSize=".7rem"
              bgcolor="#FEE2E2"
              color="#DC2626"
              borderRadius="522px"
              p="4px 21px"
            >
              {t('no-data')}
            </Box>
          </Tooltip>
        </Box>
      )}

      {/* <--- Display query and save label ---> */}
      {elementData?.updatedAt && (
        <StyledEcrfElementLabels>
          {!isEmpty(elementData?.data) && (
            <Box
              color={elementData?.status === 1 ? '#16A34A' : '#0284C7'}
              bgcolor={elementData?.status === 1 ? '#DCFCE7' : '#CFFAFE'}
              borderRadius="522px"
              p="4px 21px"
            >
              {elementData?.status === 1 ? t('verified') : t('saved')}
            </Box>
          )}

          {elementData?.comment && elementData?.status !== 1 && (
            <Box
              fontSize=".7rem"
              color="#CA8A04"
              bgcolor="#FEFCE8"
              borderRadius="522px"
              p="4px 21px"
              maxWidth="400px"
              whiteSpace="nowrap"
              overflow="hidden"
              textOverflow="ellipsis"
            >
              {t('query')}{' '}
              {DateTime.toIso(elementData?.comment?.updatedAt, DATETIME_FORMAT)}:{' '}
              {elementData?.comment?.content}
            </Box>
          )}
        </StyledEcrfElementLabels>
      )}

      {userProfile.roles.some((role) => roles.admin.includes(role)) && (
        <AdminTools element={element} id={ecrfId} data={elementData} />
      )}
    </ColoredBox>
  );
};

export default ElementBox;